























































































































import { Component, Vue, Watch, Prop } from 'vue-property-decorator';

/**
 * Helpers
 */
import {
	getDateString,
	sortByDate,
	smartDisplayMinutes,
	addMinutes,
	getLocaleDateString,
	getShortTimeString,
	getElegantDateString,
	differenceInMinutesBetweenHoursMinutes
} from "@/utils/date-helpers";

/**
 * Components
 */
import Calendar from '@/components/booking-resources/components/Calendar.vue';
import TimeRangeCreationEditionDialog from './TimeRangeCreationEditionDialog.vue';

/**
 * Types
 */
import { CalendarTimedEvent } from '@/components/booking-resources/components/Calendar.vue';
import { FilterModel } from "@/components/_global/FilterControl.vue";
import { ResourceBookableTimeRange, ResourceBooking, BookingState } from '@/api/bookings';

/** Composant d'éditeur de time ranges */
@Component({
	components: {
		'app-calendar': Calendar,
		'time-range-creation-edition-dialog': TimeRangeCreationEditionDialog,
	}
})
export default class TimeRangesEditor extends Vue {
	$refs!: {
		timeRangeCreationEditionDialog: TimeRangeCreationEditionDialog,
	};

	/** Injections */
	getLocaleDateString = getLocaleDateString;
	smartDisplayMinutes = smartDisplayMinutes;
	differenceInMinutesBetweenHoursMinutes = differenceInMinutesBetweenHoursMinutes;

	/** Constantes */

	/** Propriétés */
	@Prop()
	fetchDayBookableTimeRanges!: (day: number, month: number, year: number) => ResourceBookableTimeRange[];

	@Prop()
	fetchTimeRangesRules!: (serviceID: number) => any[];

	@Prop()
	onRemoveClick!: (timeRangeID: number) => void;

	@Prop({ default: 'fr-FR' })
	locale!: string;

	@Prop()
	services!: any[];

	@Prop({ default: false })
	isLoading!: boolean;
	@Prop({ default: false })
	isActionLoading!: boolean;


	/** Attributs */
	// Le menu du datepicker
	menu = false;

	// Les filtres
	serviceFilterModel: FilterModel | null = null;

	// La date courante sélectionnée avec le datepicker
	date: string = getDateString(new Date());
	get dateFormatted() {
		return getElegantDateString(new Date(Date.parse(this.date)), this.locale);
	}

	// Loading lorsque la date change
	isDateChangedLoading = false;

	// L'heure minimale et l'heure maximale des calendriers
	get minHour() {
		return Math.min(...this.events.map(e => parseInt(e.time.split(':')[0], 10)));
	}
	get maxHour() {
		return Math.max(...this.events.map(e => parseInt(addMinutes(e.time, e.duration - 1).split(':')[0], 10) + 1));
	}

	// Le zoom des calendriers
	minZoom = 1;
	get maxZoom() {
		if (this.events.length === 0) {
			return 1;
		}
		const minDuration = Math.min(...this.events.map((e) => e.duration));
		return Math.max(1, Math.round(2.5 * (this.maxHour - this.minHour) / minDuration));
	}
	zoom: number = 1;

	// Les créneaux réservables du jour
	dayBookableTimeRanges: ResourceBookableTimeRange[] = [];

	// La table des règles
	timeRangesRules: any[] = [];
	pagination = {
		sortBy: 'startDate',
		descending: false,
	};
	headers = [
		{
			text: '',
			value: '',
		},
		{
			text: 'time_ranges.type',
			value: 'isAvailability',
			align: 'left'
		},
		{
			text: 'time_ranges.start_time',
			value: 'startTime',
			align: 'left'
		},
		{
			text: 'time_ranges.end_time',
			value: 'endTime',
			align: 'left'
		},
		{
			text: 'time_ranges.start_date',
			value: 'startDate',
			align: 'left'
		},
		{
			text: 'time_ranges.end_date',
			value: 'endDate',
			align: 'left'
		},
		{
			text: 'time_ranges.periodicity',
			value: 'periodicity',
			align: 'left'
		},
		{
			text: 'time_ranges.bookability_step_in_minutes',
			value: 'bookabilityStepInMinutes',
			align: 'right'
		},
		{
			text: 'time_ranges.bookability_minimum_duration_in_minutes',
			value: 'bookabilityMinimumDurationInMinutes',
			align: 'right'
		},
		{
			text: 'time_ranges.bookability_maximum_duration_in_minutes',
			value: 'bookabilityMaximumDurationInMinutes',
			align: 'right'
		},
		{
			text: 'time_ranges.slicing_in_minutes',
			value: 'slicingInMinutes',
			align: 'right'
		},
	];


	// Les évènements des calendriers
	get events(): CalendarTimedEvent[] {
		if (this.selectedService == null) {
			return [];
		}
		return this.dayBookableTimeRanges.filter((x) => this.selectedService.id === x.service.id).map(
			(tr) => {
				const durationInMinutes = tr.durationInMinutes;
				const time = getShortTimeString(tr.startDate);

				return {
					title: `${addMinutes(time, 0)} - ${addMinutes(time, durationInMinutes)} (${smartDisplayMinutes(durationInMinutes)}) `,
					date: getDateString(tr.startDate),
					time: time,
					duration: durationInMinutes,
					isDisabled: !tr.isBookable,
					isReserved: false,
					isEditable: false,
					color: 'gray',
					cursor: 'default',
					isReadable: false,
					isWaitingForSynchronization: false
				};
			});
	}

	get serviceFiltersDefinitions() {
		const fd: any = {};

		if (this.services != null) {
			fd.SERVICE = {
				label: this.$t('booking_resources.resource'),
				value: (x: any) => x.label,
				key: 'id',
				items: this.services,
				f: (keySelected: string) => (x: any) => x.id === keySelected
			};
		}
		return fd;
	}

	/**
	 * Les services affichés, filtrés.
	 */
	get selectedService() {
		return this.serviceFilterModel && this.serviceFilterModel.selection.SERVICE != null ? this.services.filter(this.serviceFilterModel.predicate)[0] : null;
	}

	/** Méthodes */
	async created() {
		await this.dateChanged();
	}

	async reloadData() {
		await this.reloadTimeRangeRules();
		this.isDateChangedLoading = true;
		await this.reloadPreview();
		this.isDateChangedLoading = false;
	}

	async reloadPreview() {
		const date = new Date(this.date);

		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();

		const promises: any[] = [];
		promises.push(this.fetchDayBookableTimeRanges ? this.fetchDayBookableTimeRanges(day, month, year) : null);
		[this.dayBookableTimeRanges] = await Promise.all(promises);
	}
	async reloadTimeRangeRules() {
		if (this.selectedService != null) {
			this.timeRangesRules = await this.fetchTimeRangesRules(this.selectedService.id);
		}
		else {
			this.timeRangesRules = [];
		}
	}

	/** Méthodes - évènements */
	handleCreateClick() {
		this.$refs.timeRangeCreationEditionDialog.openCreate(this.selectedService);
	}

	async handleCreateSuccess() {
		await this.reloadTimeRangeRules();
		await this.reloadPreview();
	}
	async handleEditSuccess() {
		await this.reloadTimeRangeRules();
		await this.reloadPreview();
	}

	handleEditClick(id: number) {
		this.$refs.timeRangeCreationEditionDialog.openEdit(id);
	}

	handleRemoveClick(id: number) {
		this.$emit('onRemoveClick', id);
	}


	/** Watchers */
	/**
	 * Callback lorsque le jour sélectionné change.
	 */
	@Watch('date')
	async dateChanged() {
		this.isDateChangedLoading = true;
		await this.reloadPreview();
		this.isDateChangedLoading = false;
	}

	/**
	 * Callback lorsque le service sélectionné change
	 */
	@Watch('selectedService')
	async serviceSelectedChanged() {
		await this.reloadTimeRangeRules();
	}
}
